import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Divider,
  IconButton,
  Box,
  Tooltip,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useNotify } from 'react-admin';
import { config } from '../../config/config';
import AuditFilter from './AuditFilter';
import AuditDiffTable from './AuditDiffTable';
import type { AuditFilters } from './AuditFilter';
import type { DiffData } from './AuditDiffTable';

interface LogRecord {
  id: number;
  logTypeId: number;
  user: string;
  action: string;
  data: { data: number[] } | null;
  createdAt: string;
  updatedAt: string;
}

interface ParsedLogData {
  recordId: number | string | null;
  basePath: string;
  data: DiffData;
}

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  CREATE: { bg: '#1d8102', color: '#fff' },
  UPDATE: { bg: '#ffe457', color: '#000' },
  DELETE: { bg: '#d13212', color: '#fff' },
};

async function fetchLogTypes(token: string): Promise<Record<number, string>> {
  try {
    const res = await fetch(`${config.apiUrl}/api/log-types`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return {};
    const rows: Array<{ id: number; name: string }> = await res.json();
    return Object.fromEntries(rows.map((r) => [r.id, r.name]));
  } catch {
    return {};
  }
}

function parseLogData(record: LogRecord): ParsedLogData | null {
  if (!record?.data?.data?.length) return null;
  try {
    return JSON.parse(new TextDecoder('utf-8').decode(new Uint8Array(record.data.data)));
  } catch {
    return null;
  }
}

function getToken(): string {
  return localStorage.getItem('token') || '';
}

const PAGE_SIZE = 25;

const AuditPage: React.FC = () => {
  const notify = useNotify();
  const [records, setRecords] = useState<LogRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<LogRecord | null>(null);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [loading, setLoading] = useState(false);
  const [logTypeNames, setLogTypeNames] = useState<Record<number, string>>({});

  // Build query string from filters
  const buildQuery = useCallback(
    (currentPage: number): string => {
      const params = new URLSearchParams({
        _page: String(currentPage),
        _perPage: String(PAGE_SIZE),
        _sort: 'createdAt',
        _order: 'DESC',
      });

      if (filters.user) params.set('user', filters.user);
      if (filters.resource) params.set('action', filters.resource);
      if (filters.serial) params.set('data_like', filters.serial);
      if (filters.dateFrom) params.set('createdAt_gte', filters.dateFrom);
      if (filters.dateTo) {
        // Include the full day by advancing one day and using a strict upper bound
        const to = new Date(filters.dateTo);
        to.setDate(to.getDate() + 1);
        params.set('createdAt_lt', to.toISOString().slice(0, 10));
      }

      return params.toString();
    },
    [filters]
  );

  const load = useCallback(
    async (currentPage: number) => {
      setLoading(true);
      try {
        const token = getToken();
        const res = await fetch(`${config.apiUrl}/api/logs?${buildQuery(currentPage)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load logs');
        const data: LogRecord[] = await res.json();
        const totalCount = parseInt(res.headers.get('X-Total-Count') || '0', 10);
        setRecords(data);
        setTotal(totalCount);
      } catch {
        notify('Error loading audit logs', { type: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [buildQuery, notify]
  );

  // Load log type names once
  useEffect(() => {
    fetchLogTypes(getToken()).then(setLogTypeNames);
  }, []);

  useEffect(() => {
    load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  // Reset page when filters change
  const handleFilterChange = (newFilters: AuditFilters) => {
    setFilters(newFilters);
    setPage(1);
    setSelected(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleExport = async () => {
    try {
      const token = getToken();
      const params = new URLSearchParams(buildQuery(1));
      params.set('_perPage', '500');
      const res = await fetch(`${config.apiUrl}/api/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to export logs');
      const data: LogRecord[] = await res.json();

      if (total > data.length) {
        notify(
          `Export limited to ${data.length} rows of ${total}. Refine filters to export all data.`,
          {
            type: 'warning',
          }
        );
      }

      const rows = data.map((r) => {
        const parsed = parseLogData(r);
        return {
          id: r.id,
          type: logTypeNames[r.logTypeId] || r.logTypeId,
          user: r.user,
          resource: r.action,
          recordId: parsed?.recordId ?? '',
          createdAt: r.createdAt,
          changes: parsed ? JSON.stringify(parsed.data) : '',
        };
      });

      const header = ['id', 'type', 'user', 'resource', 'recordId', 'createdAt', 'changes'];
      const csv = [
        header.join(','),
        ...rows.map((r) =>
          header.map((k) => JSON.stringify((r as Record<string, unknown>)[k] ?? '')).join(',')
        ),
      ].join('\n');

      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notify('Export failed', { type: 'error' });
    }
  };

  const selectedParsed = selected ? parseLogData(selected) : null;
  const selectedType = selected ? logTypeNames[selected.logTypeId] : undefined;

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left panel */}
      <Box
        sx={{
          width: '40%',
          minWidth: 340,
          display: 'flex',
          flexDirection: 'column',
          borderRight: 1,
          borderColor: 'divider',
        }}
      >
        <AuditFilter filters={filters} onFilterChange={handleFilterChange} />
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            minHeight: 36,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {loading ? 'Loading…' : `${total} log${total !== 1 ? 's' : ''}`}
          </Typography>
          <Tooltip title="Export CSV">
            <span>
              <IconButton size="small" onClick={handleExport} disabled={total === 0}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Divider />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <List dense disablePadding>
            {records.map((record) => {
              const parsed = parseLogData(record);
              const typeName = logTypeNames[record.logTypeId];
              const typeColor = typeName ? TYPE_COLORS[typeName] : undefined;
              const isSelected = selected?.id === record.id;
              return (
                <React.Fragment key={record.id}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setSelected(record)}
                    sx={{ px: 1.5, py: 0.75 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {typeColor && (
                            <Chip
                              label={typeName}
                              size="small"
                              sx={{
                                backgroundColor: typeColor.bg,
                                color: typeColor.color,
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                height: 18,
                                '& .MuiChip-label': { px: 0.75 },
                              }}
                            />
                          )}
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {record.action}
                          </Typography>
                          {parsed?.recordId !== undefined && parsed?.recordId !== null && (
                            <Typography variant="caption" color="text.secondary">
                              #{parsed.recordId}
                            </Typography>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span" sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {record.user}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(record.createdAt).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
            {records.length === 0 && !loading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">
                  No audit logs found.
                </Typography>
              </Box>
            )}
          </List>
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Typography variant="caption" sx={{ mx: 1 }}>
            {page} / {totalPages}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Right panel */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {selected ? (
          <>
            <Paper
              variant="outlined"
              sx={{ p: 2, mb: 2, backgroundColor: 'grey.50', borderRadius: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {selectedType && TYPE_COLORS[selectedType] && (
                  <Chip
                    label={selectedType}
                    size="small"
                    sx={{
                      backgroundColor: TYPE_COLORS[selectedType].bg,
                      color: TYPE_COLORS[selectedType].color,
                      fontWeight: 700,
                    }}
                  />
                )}
                <Typography variant="subtitle1" fontWeight={700}>
                  {selected.action}
                  {selectedParsed?.recordId !== null && selectedParsed?.recordId !== undefined
                    ? ` #${selectedParsed.recordId}`
                    : ''}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                By <strong>{selected.user}</strong> at{' '}
                {new Date(selected.createdAt).toLocaleString()}
              </Typography>
            </Paper>
            <AuditDiffTable diff={selectedParsed?.data} />
          </>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: 'text.disabled',
            }}
          >
            <Typography variant="body1">Select a log entry to see details</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { AuditPage };
export default AuditPage;
